import React, { useEffect } from 'react';
import { IColorwayProductData, IVariant, TUserSelections } from '../../types/PDP';
import { fireFindInStoreDataLayerEvent } from 'src/scripts/utils/gtm-data-layer';
import { ShippingCountryState } from 'src/scripts/utils/shipping';
import { storeLocatorSidebarState } from '../StoreLocator/storeLocatorSidebarState';
import { observer } from 'mobx-react-lite';
import { StoreLocatorPDP } from '../StoreLocator/StoreLocatorPDP';
import { customerLocationState } from '../StoreLocator/customerLocationState';
import { findInStoreState } from './findInStoreState';
import { reaction, toJS } from 'mobx';
import { mapCountryCode } from './findInStoresUtils';

let FindInStore = ({
  selectedVariant,
  quantityAvailable,
  product,
  userSelections,
}: {
  selectedVariant: IVariant | undefined;
  quantityAvailable: number | undefined;
  product: IColorwayProductData;
  userSelections: TUserSelections;
}) => {
  const userSelectedSize = userSelections[0].Size;

  // use this list to maintain FIS supported countries
  const countriesWithAloStores: string[] = ['US', 'CA', 'UK'];
  const userCountry = mapCountryCode(ShippingCountryState.countryCode);
  const isCurrentLocaleEligibleForFis = userCountry ? countriesWithAloStores.includes(userCountry) : false;

  useEffect(() => {
    if (userCountry && selectedVariant?.sku) {
      findInStoreState.fetchStoresData(selectedVariant.sku);

      const disposeCustomerLocationChangeReaction = reaction(
        () => customerLocationState.coordinates,
        (coordinates) => {
          if (coordinates) {
            findInStoreState.fetchStoresData(selectedVariant.sku);
          }
        }
      );
      return () => {
        disposeCustomerLocationChangeReaction();
      };
    }
  }, [userCountry, selectedVariant]);

  useEffect(
    () =>
      reaction(
        () => findInStoreState.storesData,
        (storesData) => {
          // Run Heap and GTM events on storesData change
          if (storesData && selectedVariant?.sku) {
            const heapEventPayload = {
              product_title: product.title,
              color: product.colorName,
              size: userSelectedSize || '',
              sku: selectedVariant.sku,
              customer_location: {
                latitude: customerLocationState.coordinates?.latitude
                  ? parseFloat(customerLocationState.coordinates.latitude)
                  : null,
                longitude: customerLocationState.coordinates?.longitude
                  ? parseFloat(customerLocationState.coordinates.longitude)
                  : null,
              },
              quantity_available_online: quantityAvailable || 0,
              store_availability: toJS(storesData),
            };

            //switch context for events based on whether or not the sidebar is open
            // originally it was named modal but it's actually a sidebar
            const eventName = storeLocatorSidebarState.isOpen ? `find_in_store_modal_event` : `find_in_store_pdp_event`;
            fireFindInStoreDataLayerEvent(
              eventName,
              selectedVariant.sku,
              quantityAvailable,
              userSelectedSize,
              product,
              {
                latitude: customerLocationState.coordinates?.latitude
                  ? parseFloat(customerLocationState.coordinates.latitude)
                  : null,
                longitude: customerLocationState.coordinates?.longitude
                  ? parseFloat(customerLocationState.coordinates.longitude)
                  : null,
              }
            );
            heap.track(eventName, heapEventPayload);
          }
        }
      ),
    [product, quantityAvailable, selectedVariant, userSelectedSize]
  );

  const sidebarPdpData = {
    product,
    userSelectedSize,
    selectedVariant,
  };

  if (!isCurrentLocaleEligibleForFis) return null;

  return (
    <StoreLocatorPDP
      title={'Find in Store'}
      userSelectedSize={userSelectedSize}
      storeLocatorType={'fis'}
      sidebarPdpData={sidebarPdpData}
      selectedVariant={selectedVariant}
    />
  );
};

FindInStore = observer(FindInStore);
export { FindInStore };
